import React, { useState } from 'react'
import {
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Paper, Typography, Chip, TextField, Button, IconButton, Box, CircularProgress
} from '@material-ui/core'
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Undo as UndoIcon,
  Cancel as CancelIcon
} from '@material-ui/icons'

const sourceColor = {
  project: '#7b1fa2',  // purple
  client:  '#1976d2',  // blue
  entity:  '#2e7d32',  // green
  default: '#9e9e9e'   // gray
}

const InBidRateTable = ({ rates, rateType, onSave, onReset }) => {
  const [editingKey, setEditingKey] = useState(null)
  const [editValues, setEditValues] = useState({ hourlyRate: 0, defaultCrewSize: 1 })
  const [saving, setSaving] = useState(null)

  const startEdit = (row) => {
    setEditingKey(row.roleKey)
    setEditValues({
      hourlyRate: row.hourlyRate,
      defaultCrewSize: row.defaultCrewSize || 1
    })
  }

  const cancelEdit = () => {
    setEditingKey(null)
    setEditValues({ hourlyRate: 0, defaultCrewSize: 1 })
  }

  const handleSave = async (row) => {
    setSaving(row.roleKey)
    try {
      await onSave(row, editValues)
      setEditingKey(null)
    } finally {
      setSaving(null)
    }
  }

  return (
    <Paper variant="outlined">
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Source</TableCell>
              <TableCell align="right">{rateType === 'equipment' ? 'Rate ($/day)' : 'Rate ($/hr)'}</TableCell>
              {rateType === 'labor' && <TableCell align="right">Crew Size</TableCell>}
              <TableCell align="right" style={{ width: 140 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rates.map(row => {
              const isEditing = editingKey === row.roleKey
              const isSaving = saving === row.roleKey
              const displayName = row.role || row.name
              return (
                <TableRow key={row.roleKey} hover>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {displayName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {row.roleKey}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.source}
                      style={{
                        backgroundColor: sourceColor[row.source] || '#9e9e9e',
                        color: '#fff',
                        fontSize: 11,
                        height: 20
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {isEditing ? (
                      <TextField
                        type="number"
                        size="small"
                        variant="outlined"
                        value={editValues.hourlyRate}
                        onChange={(e) => setEditValues({ ...editValues, hourlyRate: parseFloat(e.target.value) || 0 })}
                        style={{ width: 100 }}
                      />
                    ) : (
                      `$${row.hourlyRate}`
                    )}
                  </TableCell>
                  {rateType === 'labor' && (
                    <TableCell align="right">
                      {isEditing ? (
                        <TextField
                          type="number"
                          size="small"
                          variant="outlined"
                          value={editValues.defaultCrewSize}
                          onChange={(e) => setEditValues({ ...editValues, defaultCrewSize: parseInt(e.target.value) || 1 })}
                          style={{ width: 70 }}
                        />
                      ) : (
                        row.defaultCrewSize || 1
                      )}
                    </TableCell>
                  )}
                  <TableCell align="right">
                    {isEditing ? (
                      <>
                        <IconButton size="small" onClick={() => handleSave(row)} disabled={isSaving}>
                          {isSaving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                        </IconButton>
                        <IconButton size="small" onClick={cancelEdit} disabled={isSaving}>
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton size="small" onClick={() => startEdit(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {row.source === 'project' && (
                          <IconButton size="small" onClick={() => onReset(row)} title="Reset to client/entity default">
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}

export default InBidRateTable
